"use client";

import React, { useState, useEffect } from "react";
import { 
  X, 
  FileSearch, 
  Building, 
  Gavel, 
  Calendar, 
  CheckCircle2, 
  AlertCircle,
  ExternalLink,
  ShieldCheck,
  Search
} from "lucide-react";
import { LegalCase } from "@/lib/demos/legalData";

interface CnrTrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CnrTrackingModal({
  isOpen,
  onClose,
}: CnrTrackingModalProps) {
  const [cnrQuery, setCnrQuery] = useState("DLHC01-084920-2026");
  const [cases, setCases] = useState<LegalCase[]>([]);
  const [selectedCase, setSelectedCase] = useState<LegalCase | null>(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetch("/api/demos/legal/cases")
        .then((res) => res.json())
        .then((data) => {
          if (data.cases && data.cases.length > 0) {
            setCases(data.cases);
            // Default select the first case matching query or first in list
            const match = data.cases.find((c: LegalCase) => c.cnr_number.toLowerCase().includes(cnrQuery.toLowerCase()));
            setSelectedCase(match || data.cases[0]);
          }
        })
        .catch(console.error);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSearched(true);
    setTimeout(() => {
      const match = cases.find(
        (c) =>
          c.cnr_number.toLowerCase().includes(cnrQuery.trim().toLowerCase()) ||
          c.case_number.toLowerCase().includes(cnrQuery.trim().toLowerCase()) ||
          c.title.toLowerCase().includes(cnrQuery.trim().toLowerCase())
      );
      setSelectedCase(match || null);
      setLoading(false);
    }, 300);
  };

  const handleQuickSelect = (caseItem: LegalCase) => {
    setCnrQuery(caseItem.cnr_number);
    setSelectedCase(caseItem);
    setSearched(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/85 backdrop-blur-md overflow-y-auto animate-fade-in">
      <div className="relative w-full max-w-2xl p-5 sm:p-7 rounded-2xl bg-slate-900 border border-slate-800 shadow-2xl text-slate-100 my-8">
        
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="flex items-center gap-2.5 pb-3 border-b border-slate-800">
          <div className="p-2 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400">
            <FileSearch className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base sm:text-lg font-serif font-bold text-white">
              eCourts CIS & CNR Matter Status Tracker
            </h3>
            <p className="text-[11px] text-slate-400">
              Live case cause-list & order sheet retrieval simulation
            </p>
          </div>
        </div>

        {/* CNR Search Bar */}
        <form onSubmit={handleSearch} className="mt-4 flex gap-2">
          <div className="relative flex-1">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
              <Search className="w-4 h-4" />
            </span>
            <input
              type="text"
              value={cnrQuery}
              onChange={(e) => setCnrQuery(e.target.value)}
              placeholder="Enter 16-Digit CNR (e.g. DLHC01-084920-2026) or Case No..."
              className="w-full pl-9 pr-4 py-2.5 bg-slate-850 border border-slate-750 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 font-mono"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs shadow-md transition-all shrink-0"
          >
            {loading ? "Locating..." : "Track"}
          </button>
        </form>

        {/* Quick Sample Presets */}
        <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px]">
          <span className="text-slate-500 font-semibold">Active Dockets:</span>
          {cases.slice(0, 3).map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => handleQuickSelect(c)}
              className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-750 text-slate-300 font-mono text-[10px] transition-colors border border-slate-700/60"
            >
              {c.cnr_number}
            </button>
          ))}
        </div>

        {/* Results Area */}
        <div className="mt-5">
          {selectedCase ? (
            <div className="p-4 rounded-xl bg-slate-850/90 border border-slate-750 space-y-4 text-xs">
              {/* Header Status */}
              <div className="flex flex-wrap items-center justify-between gap-2 pb-3 border-b border-slate-750">
                <div>
                  <span className="font-mono text-amber-400 font-bold text-sm">
                    {selectedCase.case_number}
                  </span>
                  <span className="text-[11px] text-slate-400 block font-mono">
                    CNR: {selectedCase.cnr_number}
                  </span>
                </div>
                <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30">
                  {selectedCase.stage}
                </span>
              </div>

              {/* Title & Parties */}
              <div>
                <div className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">
                  Matter Caption:
                </div>
                <h4 className="text-sm font-bold text-white mt-0.5">
                  {selectedCase.title}
                </h4>
                <div className="mt-1 text-slate-400 text-[11px]">
                  Petitioner / Appellant: <strong className="text-slate-200">{selectedCase.client_name}</strong> vs. Respondent: <strong className="text-slate-200">{selectedCase.opposing_party}</strong>
                </div>
              </div>

              {/* Hearing Details Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded-lg bg-slate-900 border border-slate-800">
                <div className="flex items-start gap-2">
                  <Building className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[10px] text-slate-400">Court & Chamber:</div>
                    <div className="text-slate-200 font-semibold">{selectedCase.court_name}</div>
                    <div className="text-[11px] text-slate-400">{selectedCase.court_room}</div>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <Gavel className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[10px] text-slate-400">Presiding Bench:</div>
                    <div className="text-slate-200 font-semibold line-clamp-1">{selectedCase.judge_bench}</div>
                    <div className="text-[11px] text-amber-400 font-mono font-bold">Item #{selectedCase.item_number}</div>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <Calendar className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[10px] text-slate-400">Next Date of Hearing (NDOH):</div>
                    <div className="text-emerald-400 font-bold font-mono">{selectedCase.next_hearing_date}</div>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <ShieldCheck className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[10px] text-slate-400">Designated Lead Counsel:</div>
                    <div className="text-slate-200 font-semibold">{selectedCase.lead_counsel}</div>
                  </div>
                </div>
              </div>

              {/* Latest Proceedings Notes */}
              {selectedCase.notes && (
                <div className="p-3 rounded-lg bg-slate-900/60 border border-slate-800/80">
                  <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-1">
                    Daily Order Sheet / Interim Directions:
                  </div>
                  <p className="text-[11px] text-slate-300 leading-relaxed italic">
                    "{selectedCase.notes}"
                  </p>
                </div>
              )}
            </div>
          ) : searched ? (
            <div className="p-8 text-center rounded-xl bg-slate-850/50 border border-slate-750 text-slate-400 space-y-2">
              <AlertCircle className="w-8 h-8 mx-auto text-amber-400/80" />
              <div className="text-sm font-semibold text-white">No Record Found for Query</div>
              <p className="text-xs max-w-sm mx-auto">
                Check that you entered a valid 16-digit CNR format or select an active docket from the presets above.
              </p>
            </div>
          ) : null}
        </div>

        {/* Modal Footer */}
        <div className="mt-5 pt-3 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-slate-500">
          <span>
            Phase 2: National Judicial Data Grid (NJDG) live sync roadmap
          </span>
          <a
            href="/demos/legal/crm"
            className="flex items-center gap-1 text-amber-400 hover:text-amber-300 font-semibold"
          >
            <span>Open in Attorney Workspace</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

      </div>
    </div>
  );
}
